import { Controller } from "@hotwired/stimulus"
import maplibregl from "maplibre-gl"
import MaplibreDraw from "maplibre-gl-draw"

export default class extends Controller {
  static targets = ["map", "countBadge", "countNumber"]
  static values = {
    sampleGeojson: String,
    queryUrl: String,
    countUrl: String,
    countyId: Number,
    searchLat: String,
    searchLng: String
  }

  connect() {
    try {
      this.#setup()
    } catch(e) {
      this.element.innerHTML = `<div class="p-4 bg-red-100 text-red-800 font-mono text-sm">${e.message}<br>${e.stack}</div>`
      console.error("county-map controller error:", e)
    }
  }

  disconnect() {
    this.map?.remove()
  }

  #setup() {
    const geojson = JSON.parse(this.sampleGeojsonValue)
    const searchLat = parseFloat(this.searchLatValue)
    const searchLng = parseFloat(this.searchLngValue)
    const hasSearch = this.searchLatValue.length > 0 && this.searchLngValue.length > 0

    const mapOptions = {
      container: this.mapTarget,
      style: "https://tiles.openfreemap.org/styles/liberty",
      zoom: 9
    }

    if (hasSearch) {
      mapOptions.center = [searchLng, searchLat]
      mapOptions.zoom = 15
    } else if (geojson.features.length > 0) {
      const coords = geojson.features.map(f => f.geometry.coordinates)
      const bounds = coords.reduce(
        (b, c) => b.extend(c),
        new maplibregl.LngLatBounds(coords[0], coords[0])
      )
      mapOptions.bounds = bounds
      mapOptions.fitBoundsOptions = { padding: 40, animate: false }
    }

    this.map = new maplibregl.Map({ ...mapOptions, doubleClickZoom: false })
    this.map.on("error", (e) => console.error("MapLibre error:", e.error))

    this.draw = new MaplibreDraw({
      displayControlsDefault: false,
      controls: { polygon: true }
    })
    this.map.addControl(this.draw)

    this.map.on("load", () => {
      this.#initSampleLayer(geojson)
      if (hasSearch) {
        new maplibregl.Marker({ color: "#C4531A" })
          .setLngLat([searchLng, searchLat])
          .addTo(this.map)
      }
    })
    this.map.on("moveend", () => this.#queryViewport())
    this.map.on("draw.create", (e) => {
      const { id, geometry } = e.features[0]
      setTimeout(() => this.draw.changeMode("direct_select", { featureId: id }), 0)
      this.#fetchCount(geometry)
    })
    this.map.on("draw.update", (e) => this.#fetchCount(e.features[0].geometry))
    this.map.on("draw.delete", () => this.countBadgeTarget.classList.add("hidden"))
  }

  async startOrder() {
    const features = this.draw.getAll().features
    if (features.length === 0) return

    const token = document.querySelector("meta[name='csrf-token']").content
    const res = await fetch("/orders", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({
        county_id: this.countyIdValue,
        polygon: JSON.stringify(features[0].geometry)
      })
    })
    const { uuid } = await res.json()
    window.location.href = `/orders/${uuid}/message`
  }

  clearPolygon() {
    this.draw.deleteAll()
    this.countBadgeTarget.classList.add("hidden")
  }

  #initSampleLayer(geojson) {
    this.map.addSource("addresses", { type: "geojson", data: geojson })
    this.map.addLayer({
      id: "address-dots",
      type: "circle",
      source: "addresses",
      paint: {
        "circle-radius": 2,
        "circle-color": "#3b82f6",
        "circle-opacity": 0.6
      }
    })
  }

  #queryViewport() {
    clearTimeout(this._queryTimeout)
    this._queryTimeout = setTimeout(() => {
      const bounds = this.map.getBounds()
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      this.#fetchForPolygon({
        type: "Polygon",
        coordinates: [[
          [sw.lng, sw.lat],
          [ne.lng, sw.lat],
          [ne.lng, ne.lat],
          [sw.lng, ne.lat],
          [sw.lng, sw.lat]
        ]]
      })
    }, 300)
  }

  async #fetchCount(geometry) {
    this.countNumberTarget.textContent = "…"
    this.countBadgeTarget.classList.remove("hidden")

    const token = document.querySelector("meta[name='csrf-token']").content
    const res = await fetch(this.countUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ polygon: JSON.stringify(geometry) })
    })
    const { count } = await res.json()
    this.countNumberTarget.textContent = count.toLocaleString()
  }

  async #fetchForPolygon(geometry) {
    const token = document.querySelector("meta[name='csrf-token']").content
    const res = await fetch(this.queryUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ polygon: JSON.stringify(geometry) })
    })
    const geojson = await res.json()
    this.map.getSource("addresses")?.setData(geojson)
  }
}
